import type { ItemFieldRow } from './item-field-reader';
import type { FormulaReadDependencies } from './formula-read-context';
import type { FormulaVariableMetadata } from './formula-variable-resolution';
import { createFormulaReadContext } from './formula-read-context';
import { scoreNamedIntegerFragmentInteger } from './formula-variable-resolution';
import { readAbilityEntryId, borrowAbilityEntryNameSource } from './inventory-ability-grant-helpers';
import { readOverrideEntryAbilityId } from './layer-cell-readers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { layerEntryNeedsResolve, resolveLayerEntry } from './layer-entry-resolver';
import { deriveDefaultProviderNames } from './provider-catalog-order';
import { appendPhpCompileArray } from './status-row-construction';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { integerFragmentBraces, integerContributionSource } from './status-integer-contribution';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpAdd, phpJsonInteger } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const has = (v: unknown, k: string): boolean => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, k);
const own = (v: unknown, k: string): unknown => has(v, k) ? (v as Row)[k] : undefined;
const path = (v: unknown, ...keys: string[]): unknown => keys.reduce(own, v);
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
const lower = (v: string): string => v.replace(/[A-Z]/g, c => c.toLowerCase());
const trim = (v: string): string => v.replace(/^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g, '');
const requireArray = (v: unknown): ItemFieldRow => { if (!arrayLike(v)) throw new TypeError('Ability essence requires a PHP array'); return v; };
const stringArg = (v: unknown): string => { if (v == null || arrayLike(v)) throw new TypeError('Ability essence key requires a scalar string'); return phpJsonString(v); };
function merge(left: ItemFieldRow, right: ItemFieldRow): unknown[] {
    const out = new Map<string, unknown>(); let next = 0;
    for (const row of [left, right]) for (const [key, value] of Object.entries(row)) {
        const n = /^(?:0|-?[1-9]\d*)$/.test(key) ? BigInt(key) : null;
        out.set(n !== null && n >= -9223372036854775808n && n <= 9223372036854775807n ? String(next++) : key, value);
    }
    return [...out.values()];
}
export interface AbilityEssenceSourceDependencies extends FormulaReadDependencies {
    metadata: FormulaVariableMetadata;
    queryFullItem: (id: string) => ItemFieldRow | null;
    resolveItemName: (id: string) => unknown;
}
/** Native passive and target-status essence walks. Conditions are consumed from
 * the preceding condition pass; source definitions and host formulas are read here.
 * Status provenance intentionally counts each persisted row once, without stack scaling.
 */
export function createAbilityEssenceSourceCollector(deps: AbilityEssenceSourceDependencies) {
    const context = createFormulaReadContext(deps), emit = (e: readonly unknown[]): void => { deps.trace?.(e); };
    const reference = (id: string): ItemFieldRow | null => { emit(['reference', id]); return deps.resolveCarrierRef(id); };
    const readers = {...context, resolveCarrierRef: reference};
    const query = (id: string): ItemFieldRow | null => { emit(['full-item', id]); return deps.queryFullItem(id); };
    const nameQuery = (id: string): unknown => { emit(['name', id]); return deps.resolveItemName(id); };
    const providers = deriveDefaultProviderNames(deps.catalogRows);
    function entryName(entry: ItemFieldRow): string {
        const name = own(entry, 'item_name'); if (typeof name === 'string' && name !== '') return name;
        const ref = own(entry, 'item_arr') ?? (has(entry, 'item_arr') ? null : own(entry, 'item_id'));
        if (typeof ref === 'string' && valid(ref)) { const value = nameQuery(ref); return typeof value === 'string' && value !== '' ? value : 'Unknown'; }
        const value = readers.itemField(borrowAbilityEntryNameSource(entry, readers), 'item_name');
        return typeof value === 'string' && value !== '' ? value : 'Unknown';
    }
    function passiveEntries(host: ItemFieldRow): unknown[] {
        let entries = readers.layerEntries(host, 'passive_ability');
        const derived = path(host, 'computed', 'creature', 'skill_ability', 'passive_ability');
        if (arrayLike(derived) && Object.keys(derived).length) {
            const seen = new Set(Object.values(entries).map(readOverrideEntryAbilityId).filter(id => id !== ''));
            for (const entry of Object.values(derived)) {
                const id = readOverrideEntryAbilityId(entry); if (id !== '' && seen.has(id)) continue;
                if (id !== '') seen.add(id); entries = appendPhpCompileArray(entries, entry);
            }
        }
        const computed = path(host, 'computed', 'creature', 'abilities', 'passive_ability');
        const merged = arrayLike(computed) && Object.keys(computed).length ? merge(entries, computed) : Object.values(entries);
        const seen = new Set<string>();
        return merged.filter(entry => { const id = readOverrideEntryAbilityId(entry); if (id === '') return true; if (seen.has(id)) return false; seen.add(id); return true; });
    }
    function levelGate(host: ItemFieldRow): ((entry: unknown, def: ItemFieldRow) => boolean) | null {
        const pc = own(host, 'player_characters'); if (!arrayLike(pc)) return null;
        const levels = new Map<string, bigint>();
        for (let slot = 1; slot <= 20; slot++) {
            if (!phpJsonTruthy(own(pc, `class_${slot}`))) continue;
            const level = phpJsonInteger(own(pc, `class_level_${slot}`));
            for (const key of [`class_${slot}`, `subclass_${slot}`]) {
                const id = phpJsonString(own(pc, key)); if (id !== '' && valid(id)) levels.set(lower(id), level);
            }
        }
        const total = phpJsonInteger(readers.layerValue(host, 'total_level'));
        return (entry, def) => {
            const required = readers.layerValue(def, 'requires_level');
            if (required != null && required !== '' && phpJsonInteger(required) > total) return false;
            const classRequired = readers.layerValue(def, 'requires_class_level');
            if (classRequired == null || classRequired === '') return true;
            const carrier = lower(phpJsonString(own(entry, 'source') ?? ''));
            let level = levels.get(carrier);
            if (carrier === '' || level === undefined) {
                level = total;
                for (const key of ['requires_subclass', 'requires_class']) {
                    const entries = readers.layerEntries(def, key); if (!Object.keys(entries).length) continue;
                    const id = lower(readLayerListEntryUuid(own(entries, '0')));
                    if (id !== '') { level = levels.get(id) ?? 0n; break; }
                }
            }
            return phpJsonInteger(classRequired) <= level;
        };
    }
    function passive(input: ItemFieldRow, rawKey: string, kind: 'passive_ability' | 'ancestry') {
        const host = requireArray(input), key = stringArg(rawKey), entries = passiveEntries(host);
        const gate = entries.length ? levelGate(host) : null;
        return collectEntries(host, key, kind, entries, true, gate);
    }
    function collectEntries(host: ItemFieldRow, key: string, kind: string, entries: unknown[], ownLayerOnly: boolean, gate: ((entry: unknown, def: ItemFieldRow) => boolean) | null, onValue?: (value: bigint) => void) {
        const lines: Row[] = [];
        let total: PhpArithmetic = 0n;
        for (const entry of entries) {
            if (own(entry, 'passive_conditions_active') === false) continue;
            let def: unknown = null, name = '', link = '';
            if (typeof entry === 'string' && valid(entry)) {
                link = entry; def = query(entry); if (!arrayLike(def)) continue;
                name = phpJsonString(readers.itemField(def, 'item_name') ?? nameQuery(entry));
            } else if (arrayLike(entry)) {
                def = own(entry, 'item_arr') ?? null; name = entryName(entry); link = readAbilityEntryId(entry, readers);
                if (name === '' && link !== '' && valid(link)) name = phpJsonString(nameQuery(link));
                if (!arrayLike(def) && link !== '' && valid(link)) { const resolved = query(link); def = phpJsonTruthy(resolved) ? resolved : []; }
                if (layerEntryNeedsResolve(def)) def = resolveLayerEntry(def, host, query);
            } else continue;
            if (gate && arrayLike(def) && !gate(entry, def)) continue;
            if (ownLayerOnly && arrayLike(def) && arrayLike(own(def, 'item_layer_types'))) {
                const layers = Object.fromEntries(Object.entries(own(def, 'item_layer_types') as ItemFieldRow));
                for (const provider of providers) delete layers[provider];
                def = {...def, item_layer_types: layers};
            }
            const fragments = arrayLike(def) ? readers.layerEntries(def, key) : [];
            if (!Object.keys(fragments).length) continue;
            const value = scoreNamedIntegerFragmentInteger(host, fragments, key, deps.metadata, readers);
            if (value === 0n) continue;
            const braces = integerFragmentBraces(Object.values(fragments)), brace = braces.length ? braces.reduce((a, b) => a > b ? a : b) : null;
            lines.push({label: trim(name) === '' ? 'Unknown source' : name, kind, value: Number(value), href_id: valid(link) ? link : '', ...(brace === null ? {} : {value_brace: Number(brace)})});
            onValue?.(value);
            total = phpAdd(total, value);
        }
        return {rolled_total: Number(total), lines};
    }
    const direct = (input: ItemFieldRow, key: string) => passive(input, key, 'passive_ability');
    const ancestralPassive = (input: ItemFieldRow, key: string) => passive(input, key, 'ancestry');
    // `gate` (2026-09-25): the PC level gate for a host's passive walk (ft_compute_collect_dynamic_integer_sources' entry_gate) —
    // null keeps every other caller ungated, as before.
    const namedEntries = (input: ItemFieldRow, entries: ItemFieldRow, key: string, kind: string, ownLayerOnly = false, onValue?: (value: bigint) => void, gate: ((entry: unknown, def: ItemFieldRow) => boolean) | null = null) => collectEntries(requireArray(input), stringArg(key), stringArg(kind), Object.values(requireArray(entries)), ownLayerOnly, gate, onValue);
    function status(input: ItemFieldRow, rawKey: string) {
        const host = requireArray(input), key = stringArg(rawKey), lines = []; let total: PhpArithmetic = 0n;
        for (const row of Object.values(readCreaturePayloadStatuses(host, readers.layerValue, reference))) {
            const def = own(row, 'status_arr'); if (!arrayLike(row) || !arrayLike(def) || (readers.itemField(def, 'item_type') ?? '') === 'creature') continue;
            const fragments = readers.layerEntries(def, key); if (!Object.keys(fragments).length) continue;
            const value = scoreNamedIntegerFragmentInteger(host, fragments, key, deps.metadata, readers);
            const braces = integerFragmentBraces(fragments), brace = braces.length ? braces.reduce((a, b) => a > b ? a : b) : null;
            if (value === 0n && brace === null) continue;
            const id = phpJsonString(readers.itemField(def, 'item_id') ?? own(row, 'status_item_id') ?? '');
            lines.push(integerContributionSource('ability', phpJsonString(readers.itemField(def, 'item_name') ?? 'Status effect'), value, id, 'status_effect', brace));
            total = phpAdd(total, value);
        }
        return {rolled_total: Number(total), lines};
    }
    function display(input: ItemFieldRow, rawName: string) {
        const host = requireArray(input), key = 'ability_' + stringArg(rawName), passive = direct(host, key), statuses = status(host, key);
        return {lines: [...passive.lines, ...statuses.lines], rolled_total: Number(phpAdd(phpJsonInteger(passive.rolled_total), phpJsonInteger(statuses.rolled_total))), rolled_total_brace: null};
    }
    return {direct, status, display, ancestralPassive, namedEntries, levelGate};
}
